import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useOwnProfile, useUpdateOwnProfile } from "../apiCalls/userApiCalls";
import { useMyClaims, useCancelClaim } from "../apiCalls/claimApiCalls";
import useAuthProtection from "../hooks/useAuthProtection";
import Loader from "../components/Loader";
import ErrorState from "../components/ErrorState";
import {
  Container,
  Card,
  Button,
  Label,
  Input,
  Select,
  Textarea,
} from "../components/ui";
import { formatNumber } from "../utils";
import { getData } from "country-list";

// ISO alpha-2 codes - what react-country-flag and the rankings filter expect.
const countries = getData();

export default function Profile() {
  useAuthProtection();

  const nameInput = useRef();
  const genderInput = useRef();
  const nationalityInput = useRef();
  const descriptionInput = useRef();

  const { isLoading, data: profile, isError } = useOwnProfile();
  const { mutate: updateProfile, isLoading: isSaving } = useUpdateOwnProfile();
  const {
    data: claims,
    isLoading: isLoadingClaims,
    isError: isClaimsError,
  } = useMyClaims();
  const { mutate: cancelClaim, isLoading: isCancelling } = useCancelClaim();

  if (isLoading) return <Loader label="Loading your profile…" />;
  if (isError || !profile)
    return <ErrorState message="Could not load your profile." />;

  const player = profile.player;

  // An account can exist without a player profile — that is what the claim
  // flow is for. What we show depends on where their claim has got to.
  if (!player) {
    // Wait for the claims before deciding what to say. The profile query can
    // resolve first, and rendering the default state in that gap would tell a
    // user with a claim under review that they have none — and hand them a
    // link to file a second one.
    if (isLoadingClaims) return <Loader label="Loading your profile…" />;

    // Not knowing is different from knowing there is nothing. Falling through
    // on an error would tell a user with a claim under review that they have
    // none, and offer to file another.
    if (isClaimsError)
      return (
        <ErrorState message="Could not check whether you have a claim in progress. Reload to try again." />
      );

    const pending = (claims || []).find((claim) => claim.status === "pending");
    const rejected = (claims || []).find((claim) => claim.status === "rejected");

    return (
      <Container className="py-10 md:py-12">
        <Card elevated className="max-w-2xl">
          {pending ? (
            <>
              <h2 className="text-2xl font-bold tracking-tight text-navy md:text-3xl">
                Claim under review
              </h2>
              <p className="mt-3 text-muted">
                You've claimed <strong className="text-navy">{pending.player?.name}</strong>.
                An administrator is checking it — you'll get your profile as
                soon as it's approved.
              </p>
              {!pending.hasDocument && (
                <p className="mt-3 rounded-sm border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-navy">
                  No photo ID attached. Claims without one take longer.
                </p>
              )}
              <Button
                type="button"
                variant="tertiary"
                className="mt-6"
                disabled={isCancelling}
                onClick={() => cancelClaim(pending._id)}
              >
                Withdraw this claim
              </Button>
            </>
          ) : rejected ? (
            <>
              <h2 className="text-2xl font-bold tracking-tight text-navy md:text-3xl">
                Claim not approved
              </h2>
              <p className="mt-3 text-muted">
                Your claim on <strong className="text-navy">{rejected.player?.name}</strong>{" "}
                wasn't approved.
              </p>
              <p className="mt-3 rounded-sm border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
                {rejected.decisionReason}
              </p>
              <Button to="/claim" className="mt-6">
                Try again
              </Button>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold tracking-tight text-navy md:text-3xl">
                No player profile yet
              </h2>
              <p className="mt-3 text-muted">
                This account isn't linked to a player profile. If you've played
                a ranked tournament, you're probably already in our register.
              </p>
              <Button to="/claim" className="mt-6">
                Find and claim your profile
              </Button>
            </>
          )}
        </Card>
      </Container>
    );
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    updateProfile({
      name: nameInput.current.value,
      gender: genderInput.current.value,
      nationality: nationalityInput.current.value,
      description: descriptionInput.current.value,
    });
  };

  return (
    <Container className="py-10 md:py-12">
      <Card elevated>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-8 md:flex-row"
        >
          {/* min-w-0 stops the form column from being sized by its widest
              control: without it the flex item takes its content width as its
              floor and the card overflows the container on narrow screens. */}
          <div className="flex min-w-0 flex-grow flex-col gap-5">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-navy md:text-4xl">
                Profile
              </h1>
              <p className="mt-2 text-sm text-muted">
                View and update your player information.
              </p>
            </div>

            {player.status !== "active" && (
              <div className="rounded-sm border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-navy">
                Your profile is awaiting activation by an administrator. It
                won't appear in the public rankings until then.
              </div>
            )}

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name"
                  type="text"
                  name="name"
                  ref={nameInput}
                  defaultValue={player.name}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                {/* Email belongs to the account, not the player, and is not
                    editable here. */}
                <Input
                  id="email"
                  type="email"
                  name="email"
                  disabled
                  defaultValue={profile.email}
                />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="gender">Gender</Label>
                <Select
                  id="gender"
                  name="gender"
                  ref={genderInput}
                  defaultValue={player.gender}
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="nationality">Nationality</Label>
                <Select
                  id="nationality"
                  name="nationality"
                  ref={nationalityInput}
                  defaultValue={player.nationality || ""}
                >
                  <option value="">-- Select your country --</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Bio</Label>
              <Textarea
                id="description"
                rows={3}
                name="description"
                ref={descriptionInput}
                defaultValue={player.description || ""}
              />
            </div>

            <div>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-center gap-4 md:w-48">
            <figure className="h-40 w-40 overflow-hidden rounded-md border border-border bg-bg md:h-48 md:w-48">
              <img
                src={
                  player.imgUrl || `${process.env.PUBLIC_URL}/default-user.jpg`
                }
                className="h-full w-full object-cover"
                alt={player.name}
              />
            </figure>
            <div className="text-center text-sm text-muted">
              <div className="tabular text-2xl font-bold text-navy">
                {formatNumber(player.rankingPoints ?? 0)}
              </div>
              points
            </div>
            <Link
              to={`/players/${player.slug}`}
              className="text-center text-sm font-semibold text-primary hover:text-primary-hover"
            >
              View public profile
            </Link>
          </div>
        </form>
      </Card>
    </Container>
  );
}
